// ClientDetailModal.jsx
// Detail view for a client. Now MULTI-TRIP aware:
//   • A trip switcher bar lets you flip between every trip on the client.
//   • "Add Trip" appends a brand-new trip (POST /crm/clients/:id/trips).
//   • Each trip can be edited (PATCH .../trips/:i) or deleted (DELETE .../trips/:i).
//   • Every tab (Trip, Payment, Refund, Flights, …) reflects the SELECTED trip.
//
// Self-contained: all trip mutations reload the client internally, so this works
// unchanged when embedded inside TripsTab. Optional onClientChanged() lets a parent
// list refresh its cards.
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { fmtDate, fmtMoney, SC } from "../utils";
import { useToast, useConfirm, Btn, Spinner, ApiError } from "../ui";
import {
  Ico, KV, Divider, PayBar, MoneyRow, StatusPill, PriorityDot, EmptyTabState, DocList,
} from "./primitives";
import { TABS, G, GL, GR, payStatusColor, tripLabel, effectivePaid, findTripIndexForPackage } from "./constants";
import { BRAND_GRADIENT } from "../theme";
import ClientRefundsTab from "./ClientRefundsTab";
import TaskList from "./TaskList";
import { createDraftRefund } from "./refundDraft";
import ServicesTimelineTab from "./ServicesTimelineTab";
import TripFormModal from "./TripFormModal";
import TripReviewTab from "../reviews/TripReviewTab";
import ClientReviewsSummary from "../reviews/ClientReviewsSummary";

// focusTrip: optional trip package (from the Trips tab) — opens on the client's trip for it.
export default function ClientDetailModal({ client: init, onClose, onEdit, onAddTask, onClientChanged, focusTrip }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [client, setClient] = useState(init);
  const [tab, setTab] = useState("profile");

  // ── Multi-trip state ──
  // Opens on the trip being viewed from the Trips tab, otherwise on the NEWEST trip.
  const [tripIndex, setTripIndex] = useState(() => {
    const list = init?.trips || [];
    const focused = findTripIndexForPackage(list, focusTrip);
    return focused !== -1 ? focused : Math.max(0, list.length - 1);
  });
  const [tripModal, setTripModal] = useState(null); // null | "add" | "edit"
  const [savingTrip, setSavingTrip] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [tLoad, setTLoad] = useState(true);
  const [tErr, setTErr] = useState(null);

  const trips = client.trips || [];
  const trip = trips[tripIndex];
  const pay = trip?.payment;
  const cur = pay?.currency || "USD";

  // Keep tripIndex in range whenever the trip count changes.
  useEffect(() => {
    if (tripIndex > trips.length - 1) setTripIndex(Math.max(0, trips.length - 1));
  }, [trips.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadClient = useCallback(async () => {
    try {
      const fresh = await api.get(`/crm/clients/${client._id}`);
      setClient(fresh);
    } catch (e) {
      toast(e.message, "error");
    }
  }, [client._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setTLoad(true);
    setTErr(null);
    api
      .get(`/crm/tasks/by-client/${client._id}`)
      .then(setTasks)
      .catch((e) => setTErr(e.message))
      .finally(() => setTLoad(false));
  }, [client._id]);

  // ── Trip mutations ──
  // One-click status change for the selected trip (same idea as the Trips tab buttons).
  const [statusSaving, setStatusSaving] = useState(null);
  const quickTripStatus = async (s) => {
    if (!trip || trip.progressStatus === s) return;
    setStatusSaving(s);
    try {
      await api.patch(`/crm/clients/${client._id}/trips/${tripIndex}`, { progressStatus: s });
      toast(`Trip status → ${s.replace(/_/g, " ")}`);
      await reloadClient();
      onClientChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setStatusSaving(null);
    }
  };

  // Create a refund drafted in the trip wizard (same record type as the Refund tab).
  const saveDraftRefund = async (refundDraft) => {
    if (!refundDraft) return;
    try { await createDraftRefund(client, refundDraft); toast("Refund created ✓"); }
    catch (e) { toast(`Trip saved, but the refund failed: ${e.message}`, "warn"); }
  };

  const addTrip = async (tripData, refundDraft) => {
    setSavingTrip(true);
    try {
      await api.post(`/crm/clients/${client._id}/trips`, tripData);
      toast("Trip added ✓");
      await saveDraftRefund(refundDraft);
      setTripModal(null);
      const newIdx = trips.length; // appended at the end
      await reloadClient();
      setTripIndex(newIdx);
      onClientChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSavingTrip(false);
    }
  };

  const saveTrip = async (tripData, refundDraft) => {
    setSavingTrip(true);
    try {
      await api.patch(`/crm/clients/${client._id}/trips/${tripIndex}`, tripData);
      toast("Trip updated ✓");
      await saveDraftRefund(refundDraft);
      setTripModal(null);
      await reloadClient();
      onClientChanged?.();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSavingTrip(false);
    }
  };

  const deleteTrip = async (idx) => {
    if (!(await confirm({ title: "Delete trip?", message: "This trip will be permanently removed from the client. This cannot be undone.", confirmText: "Delete trip" }))) return;
    try {
      await api.delete(`/crm/clients/${client._id}/trips/${idx}`);
      toast("Trip removed");
      setTripIndex(Math.max(0, trips.length - 2)); // newest remaining trip
      await reloadClient();
      onClientChanged?.();
    } catch (e) {
      toast(e.message, "error");
    }
  };

  // ── Derived counts (selected trip) ──
  const flightCount = trip?.flights?.length || 0;
  const hotelCount = trip?.hotels?.length || 0;
  const visaCount = trip?.visa?.length || 0;
  const tourCount =
    (trip?.tours?.length || 0) +
    (trip?.airportTransfers?.length || 0) +
    (trip?.insurance?.length || 0);
  const pendingTasks = tasks.filter((t) => t.status !== "completed").length;
  const hasRefund = !!trip?.refund?.refundRequested;

  const totalCost = pay?.totalTripCost || 0;
  const totalPaid = effectivePaid(pay);
  const directPaid = pay?.amountPaidToDate || 0;
  const additionalPayments = pay?.additionalPayments || [];
  const balance = totalCost - totalPaid;
  const payPct = totalCost ? Math.min(100, (totalPaid / totalCost) * 100) : 0;

  // Every payment with its date, oldest first: the direct (base-currency) payment plus
  // each other-currency payment. Shown at a glance under the totals.
  const paymentLog = [
    ...(directPaid > 0 ? [{ amount: directPaid, currency: cur, method: pay?.paymentMethod, date: pay?.date }] : []),
    ...additionalPayments.map((ap) => ({
      amount: ap.foreignAmount, currency: ap.currency || cur, base: ap.amountInBase,
      method: ap.paymentMethod, date: ap.date,
    })),
  ].sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));

  const renderPaymentDates = () =>
    (paymentLog.length > 0 || (pay?.nextPaymentDueDate && balance > 0)) && (
      <div className="mt-3 pt-3 border-t border-slate-200/70 space-y-1.5">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Payments received</p>
        {paymentLog.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-3 text-[11px]">
            <span className="min-w-0 truncate">
              <span className="font-bold text-slate-700">{fmtMoney(p.amount, p.currency)}</span>
              {p.base != null && p.currency !== cur && (
                <span className="text-slate-400 font-semibold"> → {fmtMoney(p.base, cur)}</span>
              )}
              {p.method && <span className="text-slate-400 font-semibold"> · {p.method.replace(/_/g, " ")}</span>}
            </span>
            <span className={`flex-shrink-0 font-semibold ${p.date ? "text-slate-500" : "text-slate-300 italic"}`}>
              {p.date ? `paid ${fmtDate(p.date)}` : "date not recorded"}
            </span>
          </div>
        ))}
        {pay?.nextPaymentDueDate && balance > 0 && (
          <div className="flex items-center justify-between gap-3 text-[11px]">
            <span className="font-bold text-rose-500">Next payment · {fmtMoney(balance, cur)} outstanding</span>
            <span className="flex-shrink-0 font-semibold text-rose-500">due {fmtDate(pay.nextPaymentDueDate)}</span>
          </div>
        )}
      </div>
    );

  const tabBadge = {
    flights: flightCount,
    hotels: hotelCount,
    visa: visaCount,
    services: tourCount,
    tasks: pendingTasks,
    refund: hasRefund ? 1 : 0,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3"
      style={{ background: "rgba(10,15,30,0.72)", backdropFilter: "blur(12px)" }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: "min(96vw,1060px)",
          height: "min(92vh,760px)",
          animation: "modalIn .24s cubic-bezier(.34,1.4,.64,1)",
        }}
      >
        {/* ── TOP HEADER ── */}
        <div className="flex items-center gap-4 px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div
            className="w-11 h-11 rounded-xl text-white text-lg font-black flex items-center justify-center flex-shrink-0"
            style={{
              background: BRAND_GRADIENT,
              boxShadow: `0 4px 14px ${GR}`,
            }}
          >
            {client.fullName?.charAt(0)}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-black text-slate-800 text-sm leading-tight truncate">
                {client.fullName}
              </p>
              {client.returningClient && (
                <span
                  className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: GL, color: G }}
                >
                  ↩ Returning
                </span>
              )}
            </div>
            <p className="text-[11px] text-slate-400 truncate mt-0.5">
              {client.email}
              {client.phone && <span className="text-slate-300 mx-1.5">·</span>}
              {client.phone}
              <span className="text-slate-300 mx-1.5">·</span>
              <span className="text-slate-400">
                #{client._id?.slice(-6)?.toUpperCase()}
              </span>
            </p>
          </div>

          {pay && (
            <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Paid / Total ({cur})
                  </p>
                  <p className="text-xs font-black text-slate-700">
                    <span className="text-emerald-600">{fmtMoney(totalPaid, cur)}</span>
                    <span className="text-slate-300 mx-1">/</span>
                    {fmtMoney(totalCost, cur)}
                  </p>
                </div>
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-[10px] font-black text-white flex-shrink-0"
                  style={{ background: G }}
                >
                  {Math.round(payPct)}%
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white"
              style={{ background: G }}
            >
              <Ico
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                sz={12}
                col="white"
              />
              <span className="hidden sm:inline">Edit</span>
            </button>
            <button
              onClick={onAddTask}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 transition-all"
            >
              <Ico d="M12 4v16m8-8H4" sz={12} col="#64748b" />
              <span className="hidden sm:inline">Task</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-slate-100 hover:bg-rose-50 hover:text-rose-400 flex items-center justify-center text-slate-400 transition-all"
            >
              <Ico d="M6 18L18 6M6 6l12 12" sz={13} />
            </button>
          </div>
        </div>

        {/* ── TRIP SWITCHER BAR ── */}
        <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-100 bg-slate-50/70 overflow-x-auto flex-shrink-0">
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 mr-1 flex-shrink-0">
            Trips
          </span>

          {/* Newest first — the index stays the real position in the trips array. */}
          {trips.map((tp, i) => [tp, i]).reverse().map(([tp, i]) => {
            const active = i === tripIndex;
            return (
              <button
                key={i}
                onClick={() => setTripIndex(i)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-all flex-shrink-0 border"
                style={
                  active
                    ? { background: G, color: "white", borderColor: G }
                    : { background: "white", color: "#64748b", borderColor: "#e2e8f0" }
                }
              >
                <span
                  className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ background: active ? "white" : "#cbd5e1" }}
                />
                {tripLabel(tp, i)}
              </button>
            );
          })}

          <button
            onClick={() => setTripModal("add")}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-black whitespace-nowrap transition-all flex-shrink-0 border border-dashed"
            style={{ color: G, borderColor: GR, background: GL }}
          >
            <Ico d="M12 4v16m8-8H4" sz={11} col={G} /> Add Trip
          </button>

          {trip && (
            <div className="ml-auto flex items-center gap-1.5 flex-shrink-0 pl-2">
              <button
                onClick={() => setTripModal("edit")}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-slate-500 bg-white border border-slate-200 hover:border-slate-300 transition-colors"
                title="Edit this trip"
              >
                <Ico d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" sz={11} />
                <span className="hidden sm:inline">Edit Trip</span>
              </button>
              <button
                onClick={() => deleteTrip(tripIndex)}
                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-rose-500 bg-white border border-rose-200 hover:bg-rose-50 transition-colors"
                title="Delete this trip"
              >
                <Ico d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" sz={11} />
              </button>
            </div>
          )}
        </div>

        {/* ── TAB BAR ── */}
        <div
          className="flex-shrink-0 border-b border-slate-100 bg-white"
          style={{ boxShadow: "0 1px 0 #f1f5f9" }}
        >
          <div
            className="flex overflow-x-auto px-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <style>{`.tab-scroll::-webkit-scrollbar{display:none}`}</style>
            {TABS.map((t) => {
              const active = tab === t.id;
              const badge = tabBadge[t.id];
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className="relative flex items-center gap-2 px-4 py-3.5 text-xs font-bold whitespace-nowrap transition-all flex-shrink-0"
                  style={{
                    color: active ? G : "#94a3b8",
                    borderBottom: active ? `2px solid ${G}` : "2px solid transparent",
                  }}
                >
                  <Ico d={t.icon} sz={13} col={active ? G : "#cbd5e1"} />
                  {t.label}
                  {badge > 0 && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-[9px] font-black leading-none"
                      style={{
                        background: active ? GL : "#f1f5f9",
                        color: active ? G : "#94a3b8",
                        minWidth: "1.2rem",
                        textAlign: "center",
                      }}
                    >
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── MAIN CONTENT ── */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* ══ PROFILE ══ */}
          {tab === "profile" && (
            <div className="space-y-6">
              {pay && (
                <div className="p-4 rounded-2xl border border-slate-200 bg-slate-50/50">
                  <div className="grid grid-cols-3 gap-4 text-center mb-3">
                    {[
                      ["Total Cost", fmtMoney(totalCost, cur), "text-slate-700"],
                      ["Amount Paid", fmtMoney(totalPaid, cur), "text-emerald-600"],
                      ["Balance Due", fmtMoney(balance, cur), balance > 0 ? "text-rose-500" : "text-slate-400"],
                    ].map(([l, v, c]) => (
                      <div key={l} className="flex flex-col gap-0.5">
                        <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{l}</span>
                        <span className={`text-base font-black ${c}`}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <PayBar paid={totalPaid} total={totalCost} currency={cur} />
                  <div className="flex justify-between items-center mt-2">
                    <StatusPill
                      label={pay.paymentStatus}
                      colorClass={payStatusColor[pay.paymentStatus] || "bg-slate-100 text-slate-500"}
                    />
                    <span className="text-[10px] text-slate-400 font-semibold">
                      {pay.paymentMethod?.replace(/_/g, " ")} · {cur}
                    </span>
                  </div>
                  {renderPaymentDates()}
                </div>
              )}

              <div>
                <Divider label="Personal Information" />
                <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-4">
                  <KV label="Full Name" value={client.fullName} />
                  <KV label="Email" value={client.email} />
                  <KV label="Phone" value={client.phone} />
                  <KV label="Nationality" value={client.nationality} />
                  <KV label="Country of Residence" value={client.countryOfResidence} />
                  <KV label="Client Type" value={client.returningClient ? "Returning Client" : "New Client"} />
                </div>
              </div>

              {trip && (
                <div>
                  <Divider label="Trip at a Glance" />
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-4">
                    <KV label="Services" value={trip.service} />
                    <KV label="Trip Type" value={trip.tripType} />
                    <KV label="Destinations" value={trip.destinations?.join(", ")} wide />
                    <KV
                      label="Progress"
                      value={
                        <StatusPill
                          label={trip.progressStatus}
                          colorClass={SC[trip.progressStatus] || "bg-slate-100 text-slate-500"}
                        />
                      }
                    />
                    <KV label="Start Date" value={fmtDate(trip.tripStartDate)} />
                    <KV label="End Date" value={fmtDate(trip.tripEndDate)} />
                  </div>
                </div>
              )}

              {!trips.length && (
                <div className="flex flex-col items-center justify-center py-10 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                  <span className="text-3xl mb-2">🧳</span>
                  <p className="text-sm font-bold text-slate-500 mb-1">No trips yet</p>
                  <p className="text-xs text-slate-400 mb-3">Add this client's first trip to get started</p>
                  <Btn variant="primary" size="sm" onClick={() => setTripModal("add")}>
                    <Ico d="M12 4v16m8-8H4" sz={12} col="white" /> Add Trip
                  </Btn>
                </div>
              )}

              {!tLoad && tasks.length > 0 && (
                <div>
                  <Divider label="Open Tasks" />
                  <div className="mt-3 space-y-2">
                    {tasks
                      .filter((t) => t.status !== "completed")
                      .slice(0, 4)
                      .map((t) => (
                        <div
                          key={t._id}
                          className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100"
                        >
                          <PriorityDot p={t.priority} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-700 truncate">{t.taskTitle}</p>
                            <p className="text-[10px] text-slate-400">
                              {t.category && `${t.category} · `}
                              {fmtDate(t.dueDate)}
                            </p>
                          </div>
                          <StatusPill label={t.status} colorClass={SC[t.status] || "bg-slate-100 text-slate-500"} />
                        </div>
                      ))}
                    {pendingTasks > 4 && (
                      <button
                        onClick={() => setTab("tasks")}
                        className="w-full text-center text-xs font-bold py-2 rounded-xl bg-slate-50 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        +{pendingTasks - 4} more tasks → View all
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Feedback from every trip — opens that trip's Review tab */}
              <ClientReviewsSummary
                client={client}
                onOpenTrip={(i) => { setTripIndex(i); setTab("review"); }}
              />
            </div>
          )}

          {/* ══ TRIP ══ */}
          {tab === "trip" && (
            <div className="space-y-6">
              {!trip ? (
                <EmptyTabState
                  icon="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064"
                  label="No trip recorded yet"
                />
              ) : (
                <>
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5">Update Status</p>
                    <div className="flex gap-2 flex-wrap">
                      {["planning", "in_progress", "confirmed", "completed", "cancelled"].map((s) => {
                        const on = trip.progressStatus === s;
                        return (
                          <button
                            key={s}
                            onClick={() => quickTripStatus(s)}
                            disabled={!!statusSaving}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold capitalize transition-all border disabled:opacity-60 ${
                              on
                                ? `${SC[s] || "bg-slate-100 text-slate-600"} border-transparent ring-2 ring-offset-1 ring-current`
                                : "border-slate-200 text-slate-400 hover:border-slate-300 bg-white hover:text-slate-600"
                            }`}
                          >
                            {statusSaving === s ? "Saving…" : s.replace(/_/g, " ")}
                          </button>
                        );
                      })}
                    </div>
                    {trip.tripPackageId && (
                      <p className="text-[11px] text-slate-400 mt-2">
                        Part of a group trip — changing that trip's status on the Trips tab updates this one too.
                      </p>
                    )}
                  </div>
                  <div>
                    <Divider label="Trip Details" />
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-4">
                      <KV label="Destinations" value={trip.destinations?.join(", ")} wide />
                      <KV label="Services Included" value={trip.service} wide />
                      <KV label="Trip Type" value={trip.tripType} />
                      <KV
                        label="Progress"
                        value={
                          <StatusPill
                            label={trip.progressStatus}
                            colorClass={SC[trip.progressStatus] || "bg-slate-100 text-slate-500"}
                          />
                        }
                      />
                      <KV label="Start Date" value={fmtDate(trip.tripStartDate)} />
                      <KV label="End Date" value={fmtDate(trip.tripEndDate)} />
                    </div>
                  </div>
                  {(trip.notes?.internalNotes || trip.notes?.nextFollowUpDate) && (
                    <div>
                      <Divider label="Notes & Follow-up" />
                      <div className="mt-3 space-y-3">
                        {trip.notes?.nextFollowUpDate && (
                          <div className="flex items-center gap-3 p-3.5 rounded-xl bg-sky-50 border border-sky-200">
                            <Ico d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" sz={15} col="#0ea5e9" />
                            <div>
                              <p className="text-[9px] font-black uppercase tracking-widest text-sky-500 mb-0.5">Next Follow-up</p>
                              <p className="text-sm font-bold text-sky-700">{fmtDate(trip.notes.nextFollowUpDate)}</p>
                            </div>
                          </div>
                        )}
                        {trip.notes?.internalNotes && (
                          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                            <p className="text-[9px] font-black uppercase tracking-widest text-amber-600 mb-2">Internal Notes</p>
                            <p className="text-sm text-amber-800 leading-relaxed">{trip.notes.internalNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ PAYMENT ══ */}
          {tab === "payment" && (
            <div className="space-y-6">
              {!pay ? (
                <EmptyTabState
                  icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2"
                  label="No payment information"
                />
              ) : (
                <>
                  <div>
                    <Divider label="Payment Breakdown" />
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      {[
                        ["Total Trip Cost", fmtMoney(totalCost, cur), ""],
                        ["Deposit Required", fmtMoney(pay.depositRequired, cur), ""],
                        ["Amount Paid to Date", fmtMoney(totalPaid, cur), "text-emerald-600"],
                        ["Outstanding Balance", fmtMoney(balance, cur), balance > 0 ? "text-rose-500" : "text-emerald-600"],
                        ["Payment Status", <StatusPill label={pay.paymentStatus} colorClass={payStatusColor[pay.paymentStatus] || "bg-slate-100 text-slate-500"} />, ""],
                        ["Payment Method", pay.paymentMethod?.replace(/_/g, " "), ""],
                        ["Currency", cur, "font-mono"],
                        ["Next Payment Due", fmtDate(pay.nextPaymentDueDate), ""],
                      ].map(([l, v, c]) => (
                        <KV key={l} label={l} value={<span className={c || ""}>{v}</span>} />
                      ))}
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl border border-slate-200 bg-slate-50">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-3">
                      Progress ({cur})
                    </p>
                    <PayBar paid={totalPaid} total={totalCost} currency={cur} />
                    <div className="grid grid-cols-3 gap-3 mt-4 text-center">
                      {[
                        ["Paid", fmtMoney(totalPaid, cur), "text-emerald-600"],
                        ["Balance", fmtMoney(balance, cur), balance > 0 ? "text-rose-500" : "text-slate-400"],
                        ["Total", fmtMoney(totalCost, cur), "text-slate-700"],
                      ].map(([l, v, c]) => (
                        <div key={l} className="bg-white rounded-xl py-2.5 border border-slate-100">
                          <p className={`text-sm font-black ${c}`}>{v}</p>
                          <p className="text-[9px] uppercase font-black tracking-widest text-slate-400 mt-0.5">{l}</p>
                        </div>
                      ))}
                    </div>
                    {renderPaymentDates()}
                  </div>

                  {additionalPayments.length > 0 && (
                    <div>
                      <Divider label={`Other-Currency Payments (tracked in ${cur})`} />
                      <div className="mt-3 space-y-2">
                        {additionalPayments.map((ap, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100"
                          >
                            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-[10px] font-black text-slate-500 flex-shrink-0">
                              {ap.currency || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-slate-700">
                                {fmtMoney(ap.foreignAmount, ap.currency || cur)}
                                <span className="text-slate-300 mx-1.5">→</span>
                                <span className="text-emerald-600">{fmtMoney(ap.amountInBase, cur)}</span>
                              </p>
                              <p className="text-[11px] text-slate-400 truncate">
                                {ap.paymentMethod?.replace(/_/g, " ")}
                                {ap.date && ` · paid ${fmtDate(ap.date)}`}
                                {ap.note && ` · ${ap.note}`}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div className="flex items-center justify-between px-3 pt-1">
                          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                            Direct ({cur}) {fmtMoney(directPaid, cur)} + Converted{" "}
                            {fmtMoney(totalPaid - directPaid, cur)}
                          </span>
                          <span className="text-xs font-black text-emerald-600">
                            = {fmtMoney(totalPaid, cur)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ REFUND (connected to the main Refunds section) ══ */}
          {tab === "refund" && (
            <ClientRefundsTab
              client={client}
              onAddTask={onAddTask}
              onChanged={onClientChanged}
            />
          )}

          {/* ══ REVIEW (the client's feedback for THIS trip) ══ */}
          {tab === "review" && (
            trip ? (
              <TripReviewTab client={client} tripIndex={tripIndex} trip={trip} />
            ) : (
              <EmptyTabState icon="M11.48 3.5a.56.56 0 011.04 0l2.13 5.11a.56.56 0 00.48.35l5.52.44c.5.04.7.66.32.99l-4.2 3.6a.56.56 0 00-.18.56l1.28 5.39a.56.56 0 01-.84.61l-4.73-2.89a.56.56 0 00-.59 0l-4.72 2.89a.56.56 0 01-.84-.61l1.28-5.39a.56.56 0 00-.18-.56l-4.2-3.6a.56.56 0 01.32-.99l5.52-.44a.56.56 0 00.48-.35l2.13-5.11z" label="Add a trip first" />
            )
          )}

          {/* ══ FLIGHTS ══ */}
          {tab === "flights" && (
            <div className="space-y-4">
              {!flightCount ? (
                <EmptyTabState icon="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" label="No flights added yet" />
              ) : (
                trip.flights.map((fl, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
                    <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <Ico d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" sz={15} col={G} />
                        <span className="font-black text-slate-800">{fl.airline}</span>
                        <span className="text-xs font-mono text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-lg">
                          {fl.pnr || "—"}
                        </span>
                      </div>
                      <StatusPill label={fl.status} colorClass={SC[fl.status] || "bg-slate-100 text-slate-500"} />
                    </div>
                    <div className="px-5 py-4 space-y-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                        <KV label="Booking Date" value={fmtDate(fl.bookingDate)} />
                        <KV label="Departure Date" value={fmtDate(fl.departureDate)} />
                        <KV label="Return Date" value={fmtDate(fl.returnDate)} />
                      </div>
                      <MoneyRow cost={fl.costAmt} deposit={fl.depositAmt} paid={fl.paidAmt} currency={cur} />
                      <PayBar paid={fl.paidAmt || 0} total={fl.costAmt || 0} currency={cur} />
                      <DocList docs={fl.documents} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ══ HOTELS ══ */}
          {tab === "hotels" && (
            <div className="space-y-4">
              {!hotelCount ? (
                <EmptyTabState icon="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" label="No hotels added yet" />
              ) : (
                trip.hotels.map((h, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors">
                    <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <Ico d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" sz={15} col={G} />
                        <span className="font-black text-slate-800">{h.name}</span>
                        {h.confirmation && (
                          <span className="text-xs font-mono text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-lg">
                            {h.confirmation}
                          </span>
                        )}
                      </div>
                      <StatusPill label={h.status} colorClass={SC[h.status] || "bg-slate-100 text-slate-500"} />
                    </div>
                    <div className="px-5 py-4 space-y-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                        <KV label="Booking Date" value={fmtDate(h.bookingDate)} />
                        <KV label="Check-In" value={fmtDate(h.checkIn)} />
                        <KV label="Check-Out" value={fmtDate(h.checkOut)} />
                      </div>
                      <MoneyRow cost={h.costAmt} deposit={h.depositAmt} paid={h.paidAmt} currency={cur} />
                      <PayBar paid={h.paidAmt || 0} total={h.costAmt || 0} currency={cur} />
                      <DocList docs={h.documents} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ══ VISA ══ */}
          {tab === "visa" && (
            <div className="space-y-4">
              {!visaCount ? (
                <EmptyTabState
                  icon="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  label="No visa records added yet"
                />
              ) : (
                trip.visa.map((v, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <Ico
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          sz={15}
                          col={G}
                        />
                        <span className="font-black text-slate-800 capitalize">
                          {v.type || "Tourist"} Visa
                        </span>
                      </div>
                      <StatusPill label={v.visaStatus} colorClass={SC[v.visaStatus] || "bg-slate-100 text-slate-500"} />
                    </div>
                    <div className="px-5 py-4 space-y-4">
                      <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                        <KV label="Booking Date" value={fmtDate(v.bookingDate)} />
                        <KV label="Date Submitted" value={fmtDate(v.dateSubmitted)} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          ["Required Documents", v.documentsRequired, "bg-slate-100 text-slate-500"],
                          ["Submitted Documents", v.documentsSubmitted, "bg-emerald-50 text-emerald-600"],
                        ].map(([label, docs, cls]) => (
                          <div key={label}>
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">{label}</p>
                            {docs?.length ? (
                              <div className="flex flex-wrap gap-1.5">
                                {(Array.isArray(docs) ? docs : String(docs).split(",")).map((d) => (
                                  <span key={d} className={`text-[10px] px-2 py-0.5 rounded-lg font-bold ${cls}`}>
                                    {String(d).trim().replace(/_/g, " ")}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-300">None</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <MoneyRow cost={v.costAmt} deposit={v.depositAmt} paid={v.paidAmt} currency={cur} />
                      <PayBar paid={v.paidAmt || 0} total={v.costAmt || 0} currency={cur} />
                      <DocList docs={v.documents} />
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ══ SERVICES ══ */}
          {tab === "services" && (
            <div className="space-y-6">
              {!tourCount ? (
                <EmptyTabState
                  icon="M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zm3 4h8M8 11h8M8 15h5"
                  label="No tours, transfers or insurance added yet"
                />
              ) : (
                <>
                  {trip?.tours?.length > 0 && (
                    <div>
                      <Divider label="Tours" />
                      <div className="mt-3 space-y-3">
                        {trip.tours.map((t, i) => (
                          <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                              <span className="font-bold text-slate-800 text-sm">{t.tourName}</span>
                              <StatusPill label={t.status} colorClass={SC[t.status] || "bg-slate-100 text-slate-500"} />
                            </div>
                            <div className="px-4 py-3 space-y-3">
                              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                                <KV label="Tour Date" value={fmtDate(t.tourDate)} />
                                <KV label="Booking Date" value={fmtDate(t.bookingDate)} />
                              </div>
                              <MoneyRow cost={t.costAmt} deposit={t.depositAmt} paid={t.paidAmt} currency={cur} />
                              <PayBar paid={t.paidAmt || 0} total={t.costAmt || 0} currency={cur} />
                              <DocList docs={t.documents} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {trip?.airportTransfers?.length > 0 && (
                    <div>
                      <Divider label="Airport Transfers" />
                      <div className="mt-3 space-y-3">
                        {trip.airportTransfers.map((t, i) => (
                          <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                              <span className="font-bold text-slate-800 text-sm">Transfer {i + 1}</span>
                              <StatusPill label={t.status} colorClass={SC[t.status] || "bg-slate-100 text-slate-500"} />
                            </div>
                            <div className="px-4 py-3 space-y-3">
                              <KV label="Booking Date" value={fmtDate(t.bookingDate)} />
                              <MoneyRow cost={t.costAmt} deposit={t.depositAmt} paid={t.paidAmt} currency={cur} />
                              <PayBar paid={t.paidAmt || 0} total={t.costAmt || 0} currency={cur} />
                              <DocList docs={t.documents} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {trip?.insurance?.length > 0 && (
                    <div>
                      <Divider label="Insurance" />
                      <div className="mt-3 space-y-3">
                        {trip.insurance.map((ins, i) => (
                          <div key={i} className="rounded-xl border border-slate-200 overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-800 text-sm">{ins.provider}</span>
                                {ins.policyNumber && (
                                  <span className="text-xs font-mono text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-lg">
                                    {ins.policyNumber}
                                  </span>
                                )}
                              </div>
                              <StatusPill label={ins.status} colorClass={SC[ins.status] || "bg-slate-100 text-slate-500"} />
                            </div>
                            <div className="px-4 py-3 space-y-3">
                              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                                <KV label="Booking Date" value={fmtDate(ins.bookingDate)} />
                                <KV label="Start Date" value={fmtDate(ins.startDate)} />
                                <KV label="End Date" value={fmtDate(ins.endDate)} />
                              </div>
                              <MoneyRow cost={ins.costAmt} deposit={ins.depositAmt} paid={ins.paidAmt} currency={cur} />
                              <PayBar paid={ins.paidAmt || 0} total={ins.costAmt || 0} currency={cur} />
                              <DocList docs={ins.documents} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══ TASKS ══ */}
          {tab === "servicesTimeline" && (
            <ServicesTimelineTab
              client={client}
              tripIndex={tripIndex}
              onChanged={async () => { await reloadClient(); onClientChanged?.(); }}
            />
          )}

          {tab === "tasks" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">
                  {tasks.length} task{tasks.length !== 1 ? "s" : ""} total
                </span>
                <button
                  onClick={onAddTask}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-xl text-white"
                  style={{ background: G }}
                >
                  <Ico d="M12 4v16m8-8H4" sz={11} col="white" /> Add Task
                </button>
              </div>
              {tLoad && <Spinner />}
              {tErr && <ApiError msg={tErr} />}
              {!tLoad && !tErr && (
                !tasks.length ? (
                  <EmptyTabState
                    icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                    label="No tasks for this client"
                  />
                ) : (
                  <TaskList
                    tasks={tasks}
                    onRefresh={() => {
                      setTLoad(true);
                      api
                        .get(`/crm/tasks/by-client/${client._id}`)
                        .then(setTasks)
                        .catch((e) => setTErr(e.message))
                        .finally(() => setTLoad(false));
                    }}
                  />
                )
              )}
            </div>
          )}

          {/* ══ NOTES ══ */}
          {tab === "notes" && (
            <div className="space-y-4">
              {!trip?.notes?.internalNotes && !trip?.notes?.nextFollowUpDate ? (
                <EmptyTabState
                  icon="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  label="No notes recorded"
                />
              ) : (
                <>
                  {trip?.notes?.nextFollowUpDate && (
                    <div className="flex items-start gap-4 p-4 rounded-2xl bg-sky-50 border border-sky-200">
                      <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                        <Ico
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          sz={17}
                          col="#0ea5e9"
                        />
                      </div>
                      <div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-sky-500 mb-1">Next Follow-up Date</p>
                        <p className="text-base font-black text-sky-700">{fmtDate(trip.notes.nextFollowUpDate)}</p>
                      </div>
                    </div>
                  )}
                  {trip?.notes?.internalNotes && (
                    <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Ico
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          sz={14}
                          col="#d97706"
                        />
                        <p className="text-[9px] font-black uppercase tracking-widest text-amber-600">Internal Notes</p>
                      </div>
                      <p className="text-sm text-amber-800 leading-relaxed whitespace-pre-line">
                        {trip.notes.internalNotes}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── ADD / EDIT TRIP MODAL (stacks above the detail modal) ── */}
      {tripModal === "add" && (
        <TripFormModal
          client={client}
          onSubmit={addTrip}
          onClose={() => setTripModal(null)}
          saving={savingTrip}
        />
      )}
      {tripModal === "edit" && trip && (
        <TripFormModal
          client={client}
          initial={trip}
          onSubmit={saveTrip}
          onClose={() => setTripModal(null)}
          saving={savingTrip}
        />
      )}
    </div>
  );
}